import { FONT_FAMILY } from 'config/style';
import {
  RISK_MAP_CLUSTER_BASE_SIZE,
  RISK_MAP_CLUSTER_DEFAULT_COLORS,
  RISK_MAP_CLUSTER_FONT_SIZE,
  RISK_MAP_CLUSTER_FONT_WEIGHT,
  RISK_MAP_CLUSTER_HOVER_HALO_FILL,
  RISK_MAP_CLUSTER_HOVER_HALO_OPACITY,
  RISK_MAP_CLUSTER_HOVER_HALO_OUTER_RADIUS,
  RISK_MAP_CLUSTER_HOVER_SIZE,
  RISK_MAP_CLUSTER_INNER_FILL,
  RISK_MAP_CLUSTER_LETTER_SPACING,
  RISK_MAP_CLUSTER_OUTER_BACKDROP_FILL,
  RISK_MAP_CLUSTER_OUTER_BACKDROP_RADIUS,
  RISK_MAP_CLUSTER_RING_INNER_RADIUS,
  RISK_MAP_CLUSTER_RING_OUTER_RADIUS,
  RISK_MAP_CLUSTER_SEGMENT_GAP_DEGREES,
  RISK_MAP_CLUSTER_SEGMENT_ORDER,
  RISK_MAP_CLUSTER_TEXT_COLOR,
  type RiskMapClusterBucket,
  type RiskMapClusterColors
} from './risk-map-cluster.constants';

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const FULL_TURN = 2 * Math.PI;
const QUARTER_TURN = Math.PI / 2;
const SEGMENT_GAP_RADIANS = (RISK_MAP_CLUSTER_SEGMENT_GAP_DEGREES * Math.PI) / 180;

interface BucketEntry {
  bucket: RiskMapClusterBucket;
  count: number;
}

export type RiskMapClusterCounts = Partial<Record<RiskMapClusterBucket, number>>;

export interface CreateRiskMapClusterOptions {
  hover?: boolean;
  colors?: RiskMapClusterColors;
}

const computeFontSize = (total: number): number => {
  if (total >= 1000) return 11;
  if (total >= 100) return 12;
  return RISK_MAP_CLUSTER_FONT_SIZE;
};

export const createRiskMapCluster = (counts: RiskMapClusterCounts, options: CreateRiskMapClusterOptions = {}): SVGSVGElement => {
  const hover = options.hover === true;
  const colors = options.colors ?? RISK_MAP_CLUSTER_DEFAULT_COLORS;
  const size = hover ? RISK_MAP_CLUSTER_HOVER_SIZE : RISK_MAP_CLUSTER_BASE_SIZE;
  const center = size / 2;

  const nonZero: BucketEntry[] = [];
  let total = 0;
  for (const bucket of RISK_MAP_CLUSTER_SEGMENT_ORDER) {
    const count = Math.max(0, counts[bucket] ?? 0);
    if (count > 0) {
      nonZero.push({ bucket, count });
      total += count;
    }
  }

  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
  svg.setAttribute('xmlns', SVG_NAMESPACE);

  if (hover) {
    svg.appendChild(buildHoverHalo(center));
  }

  svg.appendChild(buildOuterBackdrop(center));

  if (total === 0) {
    svg.appendChild(buildSolidRing(center, colors.notApplicable));
  } else {
    appendRingSegments(svg, center, nonZero, total, colors);
  }

  svg.appendChild(buildInnerCircle(center));
  svg.appendChild(buildCountText(center, total));

  return svg;
};

const appendRingSegments = (
  svg: SVGSVGElement,
  center: number,
  nonZero: BucketEntry[],
  total: number,
  colors: RiskMapClusterColors
): void => {
  if (nonZero.length === 1) {
    svg.appendChild(buildSolidRing(center, colors[nonZero[0].bucket]));
    return;
  }

  const availableRadians = FULL_TURN - nonZero.length * SEGMENT_GAP_RADIANS;
  const halfGap = SEGMENT_GAP_RADIANS / 2;

  let cursor = -QUARTER_TURN + halfGap;
  for (const entry of nonZero) {
    const sweep = availableRadians * (entry.count / total);
    const segmentStart = cursor;
    const segmentEnd = cursor + sweep;
    svg.appendChild(buildDonutSegment(center, segmentStart, segmentEnd, colors[entry.bucket]));
    cursor = segmentEnd + SEGMENT_GAP_RADIANS;
  }
};

const buildHoverHalo = (center: number): SVGCircleElement => {
  const halo = document.createElementNS(SVG_NAMESPACE, 'circle');
  halo.setAttribute('cx', String(center));
  halo.setAttribute('cy', String(center));
  halo.setAttribute('r', String(RISK_MAP_CLUSTER_HOVER_HALO_OUTER_RADIUS));
  halo.setAttribute('fill', RISK_MAP_CLUSTER_HOVER_HALO_FILL);
  halo.setAttribute('fill-opacity', String(RISK_MAP_CLUSTER_HOVER_HALO_OPACITY));
  return halo;
};

const buildOuterBackdrop = (center: number): SVGCircleElement => {
  const backdrop = document.createElementNS(SVG_NAMESPACE, 'circle');
  backdrop.setAttribute('cx', String(center));
  backdrop.setAttribute('cy', String(center));
  backdrop.setAttribute('r', String(RISK_MAP_CLUSTER_OUTER_BACKDROP_RADIUS));
  backdrop.setAttribute('fill', RISK_MAP_CLUSTER_OUTER_BACKDROP_FILL);
  return backdrop;
};

const buildSolidRing = (center: number, color: string): SVGCircleElement => {
  const ring = document.createElementNS(SVG_NAMESPACE, 'circle');
  ring.setAttribute('cx', String(center));
  ring.setAttribute('cy', String(center));
  ring.setAttribute('r', String(RISK_MAP_CLUSTER_RING_OUTER_RADIUS));
  ring.setAttribute('fill', color);
  return ring;
};

const buildInnerCircle = (center: number): SVGCircleElement => {
  const inner = document.createElementNS(SVG_NAMESPACE, 'circle');
  inner.setAttribute('cx', String(center));
  inner.setAttribute('cy', String(center));
  inner.setAttribute('r', String(RISK_MAP_CLUSTER_RING_INNER_RADIUS));
  inner.setAttribute('fill', RISK_MAP_CLUSTER_INNER_FILL);
  return inner;
};

const buildCountText = (center: number, total: number): SVGTextElement => {
  const text = document.createElementNS(SVG_NAMESPACE, 'text');
  text.setAttribute('x', String(center));
  text.setAttribute('y', String(center));
  text.setAttribute('text-anchor', 'middle');
  text.setAttribute('dominant-baseline', 'central');
  text.setAttribute('fill', RISK_MAP_CLUSTER_TEXT_COLOR);
  text.setAttribute('font-family', FONT_FAMILY);
  text.setAttribute('font-size', String(computeFontSize(total)));
  text.setAttribute('font-weight', String(RISK_MAP_CLUSTER_FONT_WEIGHT));
  text.setAttribute('letter-spacing', String(RISK_MAP_CLUSTER_LETTER_SPACING));
  text.textContent = total.toLocaleString();
  return text;
};

const buildDonutSegment = (center: number, startAngle: number, endAngle: number, color: string): SVGPathElement => {
  const outerRadius = RISK_MAP_CLUSTER_RING_OUTER_RADIUS;
  const innerRadius = RISK_MAP_CLUSTER_RING_INNER_RADIUS;

  const cosStart = Math.cos(startAngle);
  const sinStart = Math.sin(startAngle);
  const cosEnd = Math.cos(endAngle);
  const sinEnd = Math.sin(endAngle);

  const outerStartX = center + outerRadius * cosStart;
  const outerStartY = center + outerRadius * sinStart;
  const outerEndX = center + outerRadius * cosEnd;
  const outerEndY = center + outerRadius * sinEnd;
  const innerStartX = center + innerRadius * cosStart;
  const innerStartY = center + innerRadius * sinStart;
  const innerEndX = center + innerRadius * cosEnd;
  const innerEndY = center + innerRadius * sinEnd;

  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

  const path = document.createElementNS(SVG_NAMESPACE, 'path');
  path.setAttribute(
    'd',
    `M ${outerStartX} ${outerStartY} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEndX} ${outerEndY} L ${innerEndX} ${innerEndY} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStartX} ${innerStartY} Z`
  );
  path.setAttribute('fill', color);
  return path;
};
